import { useCallback, useRef } from 'react';
import { ApolloError } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  LeadLadderRunFieldsFragment,
  useKommoLeadLadderListQuery,
  useResumeLeadLadderRunMutation,
  useStartLadderForLeadMutation,
  useStopLeadLadderRunMutation,
} from 'core/graphql';
import { ToastTypes } from 'types';

import { usePageVisiblePoll } from '../Meta/usePageVisiblePoll';
import { leadCardTargets, useQuietRefetch } from './leadRefetch';
import {
  asClause,
  ILeadError,
  useLeadErrorText,
  useLeadsNotify,
} from './leadsFeedback';
import { leadLadderWarningKey, leadSmsReasonKey } from './leadsModel';
import { useLeadPermissions } from './useLeadPermissions';

/**
 * ТЗ 0084, этап 2 — SMS-лесенки лида в карточке: запуски (последние 10),
 * «Поставить на лесенку», стоп, возобновление.
 */

export type ILeadLadderRun = LeadLadderRunFieldsFragment;

const EMPTY_RUNS: ILeadLadderRun[] = [];

/** Идущий запуск перечитывается: следующее SMS, «ушло», анкета пришла. */
export const LEAD_LADDER_POLL_MS = 60000;

export const useLeadLadders = (leadId: string | null, enabled = true) => {
  const { canUse } = useLeadPermissions();
  const skip = !canUse || !enabled || !leadId;
  const {
    data,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = useKommoLeadLadderListQuery({
    variables: { leadId: leadId || '' },
    skip,
    fetchPolicy: 'cache-and-network',
  });
  const runs = data?.kommoLeadLadders ?? EMPTY_RUNS;
  const active = runs.some((run) => run.status === 'active');
  usePageVisiblePoll(
    startPolling,
    stopPolling,
    LEAD_LADDER_POLL_MS,
    !skip && active,
  );
  const reload = useCallback(() => {
    refetch()?.catch(() => undefined);
  }, [refetch]);
  return {
    runs,
    loading: loading && !data,
    failed: Boolean(error) && !data,
    reload,
  };
};

type RawErrors =
  | ReadonlyArray<{
      field?: string | null;
      code?: string | null;
      message?: string | null;
    } | null>
  | null
  | undefined;

export const toLeadErrors = (raw: RawErrors): ILeadError[] =>
  (raw ?? [])
    .filter((error): error is NonNullable<typeof error> => Boolean(error))
    .map((error) => ({
      field: error.field ?? '',
      code: (error.code ?? '').toUpperCase(),
      message: error.message ?? '',
    }));

export const leadCrashError = (caught: unknown): ILeadError => ({
  field: '',
  code:
    caught instanceof ApolloError && caught.graphQLErrors.length > 0
      ? 'NO_ACCESS'
      : 'NETWORK',
  message: caught instanceof Error ? caught.message : '',
});

export interface ILeadLadderResult {
  ok: boolean;
  errors: ILeadError[];
  /** Почему лесенка сейчас не шлёт (запуск создан всё равно). */
  warning: string | null;
}

/**
 * После УДАЧНОГО действия с лесенкой перечитываются: запуски, состояние SMS
 * (первое SMS уходит сразу), карточка (чип лесенки в списке — та же запись
 * `KommoLeadType:<id>`) и журнал — следом, без ожидания: сбой перечитывания
 * не превращает удачный запуск в «Не удалось» (перепроверка 0084).
 */
const AFTER_LADDER = ['ladders', 'sms', 'card', 'events'] as const;

/**
 * Отказы, после которых экран отстал от сервера: лесенка уже закончилась
 * (срок вышел, анкета пришла) — список запусков перечитывается, чтобы
 * «Остановить» пропала.
 */
const STALE_CODES = ['NOTHING_TO_STOP', 'NOT_STOPPED'];

export const useLeadLadderActions = (leadId: string) => {
  const { t } = useTranslation();
  const notify = useLeadsNotify();
  const errorText = useLeadErrorText();
  const refetch = useQuietRefetch();
  const [startMut, { loading: starting }] = useStartLadderForLeadMutation();
  const [stopMut, { loading: stopping }] = useStopLeadLadderRunMutation();
  const [resumeMut, { loading: resuming }] = useResumeLeadLadderRunMutation();
  const inFlight = useRef(false);

  /**
   * Почему лесенка не шлёт — ТЕМИ ЖЕ словами, что шапка формы SMS лида
   * (`leads.smsReasons`); свои слова — только у причин самой лесенки (план,
   * робот). Предложением, с заглавной; во фразу — через `asClause`.
   */
  const warningText = useCallback(
    (warning: string | null | undefined) => {
      if (!warning) return '';
      const key = leadSmsReasonKey(warning) || leadLadderWarningKey(warning);
      return key ? t(key) : warning;
    },
    [t],
  );

  const run = useCallback(
    async (
      action: () => Promise<{
        payload:
          | {
              success?: boolean | null;
              customErrors?: RawErrors;
              warning?: string | null;
            }
          | null
          | undefined;
      }>,
      okTitle: string,
      // Окно запуска рисует отказ у себя — тост его бы только повторил.
      { quietErrors = false }: { quietErrors?: boolean } = {},
    ): Promise<ILeadLadderResult> => {
      if (inFlight.current) return { ok: false, errors: [], warning: null };
      inFlight.current = true;
      try {
        const { payload } = await action();
        const errors = toLeadErrors(payload?.customErrors);
        if (!payload?.success || errors.length) {
          if (errors.some((error) => STALE_CODES.includes(error.code))) {
            refetch(leadCardTargets(leadId, ['ladders', 'card']));
          }
          if (!quietErrors) {
            notify(
              ToastTypes.error,
              t('leads.ladders.toast.failed'),
              errors.map((error) => errorText(error)).join('\n') || undefined,
            );
          }
          return { ok: false, errors, warning: null };
        }
        refetch(leadCardTargets(leadId, AFTER_LADDER));
        const warning = payload.warning ?? null;
        notify(
          warning ? ToastTypes.warning : ToastTypes.success,
          okTitle,
          warning
            ? t('leads.ladders.toast.warning', {
                reason: asClause(warningText(warning)),
              })
            : undefined,
        );
        return { ok: true, errors: [], warning };
      } catch (caught) {
        const error = leadCrashError(caught);
        if (!quietErrors) {
          notify(
            ToastTypes.error,
            t('leads.ladders.toast.failed'),
            errorText(error),
          );
        }
        return { ok: false, errors: [error], warning: null };
      } finally {
        inFlight.current = false;
      }
    },
    [errorText, leadId, notify, refetch, t, warningText],
  );

  const start = useCallback(
    (kind: string, days: number | null, requestKey: string) =>
      run(
        async () => {
          const { data } = await startMut({
            variables: { leadId, kind, days, requestKey },
          });
          return { payload: data?.startLeadLadder };
        },
        t('leads.ladders.toast.started'),
        { quietErrors: true },
      ),
    [leadId, run, startMut, t],
  );

  const stop = useCallback(
    (runId: string) =>
      run(async () => {
        const { data } = await stopMut({ variables: { runId } });
        return { payload: data?.stopLeadLadder };
      }, t('leads.ladders.toast.stopped')),
    [run, stopMut, t],
  );

  const resume = useCallback(
    (runId: string) =>
      run(async () => {
        const { data } = await resumeMut({ variables: { runId } });
        return { payload: data?.resumeLeadLadder };
      }, t('leads.ladders.toast.resumed')),
    [resumeMut, run, t],
  );

  return {
    start,
    stop,
    resume,
    warningText,
    busy: { starting, stopping, resuming },
  };
};
